import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useConversations } from "@/api/hooks/use-conversations";
import useAuthStore from "@/store/use-auth-store";
import { moduleRegistry, type ModuleId, type ModuleInstance } from "@/modules";
import { useChatDockStore } from "@/features/chat-dock/store/chat-dock-store";
import { useModuleHub } from "@/features/module-hub/hooks/use-module-hub";
import type { VeilRect } from "@/features/module-hub/store/module-transition-store";
import { useHomeStore } from "../store/home-store";

/** "Yeni Sohbet" → Launchpad. Tek kaynak; sidebar butonu da buradan okur. */
export const LAUNCHPAD_PATH = "/new";

const RECENT_LIMIT = 4;
/** Bu kadar ziyaretten sonra hero kısalır. */
const RETURNING_AFTER = 2;

export interface RecentChat {
  id: string;
  name: string;
  updatedAt: string;
  module?: ModuleInstance;
}

/**
 * Anasayfanın tek okuma noktası. Bölümler, yörünge ve kinetik başlık prop
 * almaz, buradan okur; bu yüzden yan etki içermez — ziyaret sayacı ve
 * cleanup HomePage'de.
 *
 * - `modules`: registry sırası (aktifler önce) — yörünge ve bento aynı liste
 * - `hoveredId` / `setHovered`: store'da; hover → sayfa teması o modüle kayar
 * - `enter(instance, origin)`: perde geçişiyle modüle gir
 * - `recent`: son sohbetler, modül etiketiyle
 * - `isReturning`: alışkın kullanıcı → kompakt hero
 */
export const useHome = () => {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const { modules, select, hoveredId, setHovered, isTransitioning, selectedId } =
    useModuleHub();
  const visits = useHomeStore((s) => s.visits);
  const isReturning = visits > RETURNING_AFTER;

  const enter = useCallback(
    (instance: ModuleInstance, origin: HTMLElement | VeilRect | null) =>
      select(instance, origin),
    [select],
  );

  // Son sohbetler — hub sidebar'ı ile aynı query (modül filtresiz), cache paylaşılır
  const { data } = useConversations(
    { limit: 12, sortBy: "-createdAt" },
    { enabled: isAuthenticated },
  );
  const recent = useMemo<RecentChat[]>(
    () =>
      (data?.pages ?? [])
        .flatMap((p) => p.data?.result ?? [])
        .slice(0, RECENT_LIMIT)
        .map((c) => {
          // Sunucu serbest string döner; registry'de yoksa modülsüz sayılır
          const moduleId = (c.module ?? undefined) as ModuleId | undefined;
          return {
            id: c._id,
            name: c.name || t("chat.untitled"),
            updatedAt: c.updatedAt || c.createdAt,
            module: moduleId ? moduleRegistry.getModuleById(moduleId) : undefined,
          };
        }),
    [data, t],
  );

  /** Sohbet modüllüyse modülün kökü (dock orada açılır), değilse tam sayfa. */
  const chatHref = useCallback(
    (chat: RecentChat) =>
      chat.module ? chat.module.basePath : `/chat/${chat.id}`,
    [],
  );
  const openChat = useCallback((chat: RecentChat) => {
    if (!chat.module) return;
    const dock = useChatDockStore.getState();
    dock.setModuleChat(chat.module.id, chat.id);
    dock.open();
  }, []);

  return {
    modules,
    hoveredId,
    setHovered,
    enter,
    isTransitioning,
    selectedId,
    isReturning,
    isAuthenticated,
    recent,
    chatHref,
    openChat,
    launchpadPath: LAUNCHPAD_PATH,
  };
};
